import { useEffect, useRef } from 'react'
import { useRecoilCallback } from 'recoil'
import {
  canvasPageIdAtom,
  isEditorReadyAtom,
  itemAtomFamily,
  itemIdAtom,
  selectedItemsAtom,
  type EditorItem,
} from '@/atoms/_editorAtoms'
import {
  editorTextBoxDraftsAtom,
  editorTextBoxPendingDeletesAtom,
} from '@/atoms/editorTextBoxAtoms'
import { useEditorPageContent } from '@/hooks/editor/useEditorPageContent'
import {
  hydrationWrites,
  type HydrationMemory,
} from '@/utils/editor/canvasHydration'
import { hydrateEditorTextBoxItems } from '@/utils/editor-text-box/editorTextBoxAdapter'
import { sameStringList } from '@/utils/editor/sameStringList'
import { selectionOnCanvas } from '@/utils/editor/selectionOnCanvas'

type CanvasItem = EditorItem & { id: string }

/** The part of a page's saved product the canvas is hydrated from. */
export type HydratedPageProduct = {
  orderPageId?: string | null
  items?: CanvasItem[] | null
}

/**
 * Loads the canvas from the page the editor shows: whenever the page's
 * saved items, its id or its text-box bundle change, every canvas item atom
 * and the canvas item list are hydrated from them. Page.js runs it for the
 * page on screen, so a canvas change that was not written back to the saved
 * page is undone the next time that page's items change.
 *
 * The unsaved text-box drafts and pending deletes are read off the store at
 * hydration time rather than subscribed to: a subscription would rebuild the
 * canvas on every draft commit or delete, wiping the items the page JSON has
 * not heard of yet.
 */
export const useHydrateCanvasFromPage = (
  product: HydratedPageProduct | null | undefined,
) => {
  const pageId = product?.orderPageId
  const items = product?.items
  const editorPageContent = useEditorPageContent(pageId)

  // What each item's JSON said at the last hydration, so a re-hydration can
  // tell a JSON change from an atom that moved on its own.
  const hydrationMemoryRef = useRef<HydrationMemory>(new Map())

  const loadInitialData = useRecoilCallback(
    ({ set, snapshot }) =>
      (
        pageItems: CanvasItem[],
        canvasPageId: string | null | undefined,
        bundle: typeof editorPageContent,
      ) => {
        const drafts = snapshot
          .getLoadable(editorTextBoxDraftsAtom)
          .valueMaybe()
        const pendingDeletes = snapshot
          .getLoadable(editorTextBoxPendingDeletesAtom)
          .valueMaybe()
        const hydrated = hydrateEditorTextBoxItems(
          pageItems,
          bundle,
          drafts,
          pendingDeletes,
        ) as CanvasItem[]
        const ids = hydrated.map((item) => item.id)
        // An atom that moved on its own since the last hydration (a box's
        // load result, an author's edit) is kept when its JSON item is
        // unchanged; only a JSON item that changed is written over it.
        for (const item of hydrationWrites(
          hydrated,
          hydrationMemoryRef.current,
          (itemId) => snapshot.getLoadable(itemAtomFamily(itemId)).valueMaybe(),
        )) {
          set(itemAtomFamily(item.id), item)
        }
        const currentIds = snapshot.getLoadable(itemIdAtom).valueMaybe()
        if (!sameStringList(currentIds, ids)) set(itemIdAtom, ids)
        const selectedIds =
          snapshot.getLoadable(selectedItemsAtom).valueMaybe() ?? []
        const selectedOnCanvas = selectionOnCanvas(selectedIds, ids)
        if (selectedOnCanvas !== selectedIds)
          set(selectedItemsAtom, selectedOnCanvas)
        // Record WHICH page these items belong to. Writes back into
        // editorPagesAtom target a page by index and read their content from
        // the canvas; comparing this stamp to the write target is what stops a
        // not-yet-rehydrated canvas being persisted onto the wrong page.
        const stamp = canvasPageId ?? null
        if (snapshot.getLoadable(canvasPageIdAtom).valueMaybe() !== stamp)
          set(canvasPageIdAtom, stamp)
        // The current page's items are now hydrated into itemIdAtom: mark the
        // editor ready so Save enables and the save guard can tell this
        // hydrated state apart from the transient un-hydrated re-init window.
        if (snapshot.getLoadable(isEditorReadyAtom).valueMaybe() !== true)
          set(isEditorReadyAtom, true)
      },
    [],
  )

  useEffect(() => {
    if (items) loadInitialData(items, pageId, editorPageContent)
  }, [items, pageId, editorPageContent, loadInitialData])
}
